/**
 * SnapshotsPanel — renders the latest analytics_snapshots rows for each
 * of the 4 clusters (growth / engagement / safety / revenue). These are
 * the lightweight founder-dashboard time series, distinct from the full
 * cluster MV export in AnalyticsPanel.
 */
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  useAnalyticsSnapshots,
  SNAPSHOT_CLUSTERS,
  SNAPSHOT_LABELS,
  type SnapshotCluster,
} from "@/lib/founder/useAnalyticsSnapshots";

const fmtTs = (iso: string) => new Date(iso).toLocaleString();

const SnapshotList = ({ cluster }: { cluster: SnapshotCluster }) => {
  const { rows, loading } = useAnalyticsSnapshots(cluster, 20);
  if (loading) return <p className="text-sm text-muted-foreground py-4">Loading…</p>;
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No snapshots recorded yet for {SNAPSHOT_LABELS[cluster]}.
      </p>
    );
  }
  const latest = rows[0];
  const latestKeys = Object.keys(latest.metrics ?? {});
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border p-4 bg-muted/30">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
          Latest · {fmtTs(latest.taken_at)}
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {latestKeys.map((k) => (
            <div key={k}>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                {k.replace(/_/g, " ")}
              </p>
              <p className="text-lg font-semibold tabular-nums">
                {String((latest.metrics as Record<string, unknown>)[k] ?? "—")}
              </p>
            </div>
          ))}
        </div>
      </div>
      {rows.length > 1 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="py-1.5 pr-4 font-medium">Taken at</th>
                {latestKeys.map((k) => (
                  <th key={k} className="py-1.5 pr-4 font-medium">
                    {k}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(1).map((r) => (
                <tr key={r.id} className="border-b border-border/40">
                  <td className="py-1.5 pr-4 font-mono">{fmtTs(r.taken_at)}</td>
                  {latestKeys.map((k) => (
                    <td key={k} className="py-1.5 pr-4 font-mono tabular-nums">
                      {String((r.metrics as Record<string, unknown>)[k] ?? "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SnapshotsPanel = () => {
  return (
    <Card className="p-5 space-y-4">
      <div>
        <h3 className="text-sm font-medium">Cluster snapshots</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Periodic snapshots of the 4 founder clusters (analytics_snapshots).
          Independent of the full investor packet export above.
        </p>
      </div>
      <Tabs defaultValue={SNAPSHOT_CLUSTERS[0]}>
        <TabsList>
          {SNAPSHOT_CLUSTERS.map((c) => (
            <TabsTrigger key={c} value={c}>
              {SNAPSHOT_LABELS[c]}
            </TabsTrigger>
          ))}
        </TabsList>
        {SNAPSHOT_CLUSTERS.map((c) => (
          <TabsContent key={c} value={c}>
            <SnapshotList cluster={c} />
          </TabsContent>
        ))}
      </Tabs>
    </Card>
  );
};

export default SnapshotsPanel;
